// The application shell's navigation rail — one sidebar for every role, built
// from nav-model.ts as a MUD `mud-sidebar` of `mud-sidebar-group` sections and
// `mud-sidebar-item` rows. From 1024px up it is a sticky column the user can
// collapse to MUD's icon rail, at MUD's own widths (368 expanded, 68 collapsed:
// owner ruling M1, wireframes/00-decisions.md); below 1024px the same element is
// the off-canvas panel over a scrim, because `mud-sidebar` has no off-canvas
// mode (owner ruling M2). The panel and the rail are one render of one model.
//
// The whole rail is the sidebar's content, not only its rows: the brand head, the
// Registrar selector and the account block sit in the same surface, in the order
// they always had, and `mud-sidebar`'s own `<nav>` is the one navigation landmark.
// per docs/ui-shape.md §Sidebar application shell (owner decision, 2026-08-26)
// per docs/shape-ops-mud-1-2-migration.md
import { useEffect, useRef } from 'react';
import type { ElementRef, MouseEvent } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslate } from '@tolgee/react';
import {
  MudBadge,
  MudButton,
  MudIcon,
  MudSidebar,
  MudSidebarGroup,
  MudSidebarItem,
} from '@egov-moldova/mud-react';
import { RouterLink } from '@simsm/ui-shared';
import { useSession } from '../../hooks/use-session';
import { useDraftsCount } from '../../features/reporting/use-drafts-count';
import { useFiringAlertsCount } from '../../features/alerts/use-firing-alerts-count';
import { usePendingCorrectionsCount } from '../../features/corrections/use-pending-corrections-count';
import { useMyCorrectionCounts } from '../../features/corrections/use-my-correction-counts';
import { useActiveRegistrar } from '../../hooks/use-active-registrar';
import { useNavContext } from '../../hooks/use-nav-context';
import { RegistrarSelector } from '../RegistrarSelector/RegistrarSelector';
import { SidebarAccount } from './SidebarAccount';
import {
  ALERT_DASHBOARD_SEGMENT,
  activeSegment,
  isReporter,
  isSystemAdmin,
  navSectionsFor,
} from './nav-model';
import type { AttentionKey, NavItem } from './nav-model';
import { patchSidebarA11y } from './mud-sidebar-a11y';

const ATTENTION_ARIA_KEYS: Record<AttentionKey, string> = {
  drafts: 'nav.attention.drafts',
  corrections: 'nav.attention.corrections',
  alerts: 'nav.attention.alerts',
};

interface SidebarProps {
  // Desktop icon rail. Never true while the off-canvas panel is showing, so the
  // panel always renders labels.
  collapsed: boolean;
  onToggleCollapsed: () => void;
  // Off-canvas panel below 1024px.
  offCanvas: boolean;
  open: boolean;
  onClose: () => void;
}

export function Sidebar({
  collapsed,
  onToggleCollapsed,
  offCanvas,
  open,
  onClose,
}: SidebarProps): JSX.Element {
  const { t } = useTranslate();
  const location = useLocation();
  const navigate = useNavigate();
  const railRef = useRef<ElementRef<typeof MudSidebar>>(null);
  const { role } = useParams<{ role: string }>();
  const rolePrefix = `/${role ?? 'operator'}`;
  const session = useSession().data;
  const activeRole = session?.activeRole;

  // caiet UC10-02 + arbitration D1: Stocuri IMSP is decided by the *kind* of the
  // active Registrar, not by the role alone; the route guard reads the same
  // context (docs/shape-ops-tenant-selector.md §7.2).
  const { context: navContext } = useNavContext();
  const groups = navSectionsFor(activeRole, navContext);
  const items = groups.flatMap((group) => group.items);
  const segment = activeSegment(
    location.pathname,
    role ?? 'operator',
    items.map((item) => item.to),
  );

  // The alert count belongs to the System Administrator's "Alerte si notificari" entry, and to
  // nobody else since owner decision D7 removed the observer's inbox. It counts the ACTIVE
  // (firing) alerts, as the predefined alert dashboard does (NC-MENU-18, CF-46), not unread
  // events. Asking for it as an observer polled the rules every 30s for a badge no rail row carries.
  const readsAlerts = activeRole !== undefined && isSystemAdmin(activeRole);
  // A reporter's counts are the active Registrar's: none is asked for before
  // one is chosen (T168), and the choice's query reset fetches them.
  const { awaitingChoice } = useActiveRegistrar();
  const reports = activeRole !== undefined && isReporter(activeRole) && !awaitingChoice;
  const { count: drafts } = useDraftsCount(reports);
  // One AttentionKey, two tenants. `approvals` counts everything waiting on the
  // System Administrator; `corrections` counts what the reporter themselves has
  // waiting (owner decision D8). Neither role can read the other's endpoint, so
  // the two hooks are gated by role and only one of them ever fetches.
  const { count: adminCorrections } = usePendingCorrectionsCount(
    activeRole !== undefined && isSystemAdmin(activeRole),
  );
  const { counts: myCorrections } = useMyCorrectionCounts(reports);
  const { count: alerts } = useFiringAlertsCount(readsAlerts);

  function attentionCount(item: NavItem): number {
    if (item.attention === 'drafts') return drafts;
    if (item.attention === 'corrections') {
      return item.to === 'approvals' ? adminCorrections : myCorrections.PENDING;
    }
    if (item.attention === 'alerts') return alerts;
    return 0;
  }

  // The rows are `<a href>` inside the item's shadow root, so a bare click is a full
  // page load. Only the click react-router's own <Link> intercepts is taken: an
  // unmodified primary one. Ctrl/Cmd/Shift/Alt-click and a middle click stay native
  // (a new tab or window), and a copied address is the real one. The item's
  // `mudSelect` carries no event to inspect, so the click itself is read.
  function openInApp(event: MouseEvent<HTMLElement>, path: string): void {
    if (event.defaultPrevented) return;
    if (event.button !== 0 || event.metaKey || event.altKey || event.ctrlKey || event.shiftKey) {
      return;
    }
    event.preventDefault();
    navigate(path);
    onClose();
  }

  // The three gaps in `mud-sidebar`'s shadow DOM that the markup below relies on
  // closing: see mud-sidebar-a11y.ts. Re-run after every render because the rows
  // (and the attributes on them) change with the role, the counts and the collapse.
  useEffect(() => {
    const rail = railRef.current;
    return rail ? patchSidebarA11y(rail) : undefined;
  });

  function renderRow(item: NavItem): JSX.Element {
    const path = `${rolePrefix}/${item.to}`;
    const label = t(item.labelKey);
    const active = item.to === segment;
    const count = attentionCount(item);
    const attention = count > 0 ? item.attention : undefined;
    const alertsLink = attention === 'alerts';
    const countText = attention ? t(ATTENTION_ARIA_KEYS[attention], { count }) : undefined;
    // The collapsed rail draws no label, so the row is named explicitly; a counted row
    // is named with its count in both states (the count is its attention signal).
    const counted = attention !== undefined && !alertsLink;
    const accessibleName =
      collapsed || counted
        ? [label, counted ? countText : undefined].filter(Boolean).join(', ')
        : undefined;
    const badge = attention ? (
      <MudBadge
        className="simsm-sidebar__item-badge"
        type={collapsed ? 'dot' : 'numbered'}
        variant="warning"
        size={collapsed ? 'sm' : 'md'}
        count={count}
        aria-label={countText}
      />
    ) : null;
    return (
      <div
        key={item.to}
        className="simsm-sidebar__entry"
        {...(alertsLink ? { role: 'listitem' } : {})}
      >
        <MudSidebarItem
          value={item.to}
          data-segment={item.to}
          data-accessible-name={accessibleName}
          data-overlay-link={alertsLink ? '' : undefined}
          className={alertsLink ? 'simsm-sidebar__item--with-badge-link' : undefined}
          icon={item.icon}
          label={label}
          href={path}
          active={active}
          title={collapsed ? label : undefined}
          onClick={(event: MouseEvent<HTMLElement>) => openInApp(event, path)}
        >
          {/* A counted row slots its label and the count together, the count at the row's
              end, as the wireframe's nav item draws it (Shell.tsx:96-105). The collapsed
              rail hides the slot; its dot is drawn over the icon below. */}
          {counted && !collapsed ? (
            <span className="simsm-sidebar__item-content">
              <span className="simsm-sidebar__item-text">{label}</span>
              {badge}
            </span>
          ) : undefined}
        </MudSidebarItem>
        {/* NC-MENU-18: the entry reaches alert management, its indicator leads to the
            predefined alert dashboard. A link cannot hold a link, so the indicator is a
            sibling laid over the row's end, in the collapsed rail too. */}
        {alertsLink ? (
          <RouterLink
            to={`${rolePrefix}/${ALERT_DASHBOARD_SEGMENT}`}
            className="simsm-sidebar__badge-link"
            variant="strict"
            underline="none"
            aria-label={t('nav.attention.alerts_link', { count })}
            onClick={onClose}
          >
            {badge}
          </RouterLink>
        ) : counted && collapsed ? (
          <span className="simsm-sidebar__dot" aria-hidden="true">
            {badge}
          </span>
        ) : null}
      </div>
    );
  }

  return (
    <MudSidebar
      ref={railRef}
      id="primary-navigation"
      className="simsm-sidebar"
      aria-label={t('nav.primary.aria_label')}
      aria-hidden={offCanvas && !open ? true : undefined}
      collapsed={collapsed}
    >
      <div className="simsm-sidebar__head">
        <Link to={`${rolePrefix}/home`} className="simsm-sidebar__brand">
          <img
            src="/brand/amdm-mark.png"
            alt={t('home.amdm.logo_alt')}
            className="simsm-sidebar__brand-mark"
            width={32}
            height={32}
          />
          <span className="simsm-sidebar__brand-name">{t('home.product.mark')}</span>
        </Link>
        {offCanvas ? (
          <MudButton
            shape="circular"
            variant="strict"
            appearance="text"
            size="sm"
            iconOnly
            label={t('nav.drawer.close')}
            onClick={onClose}
          >
            <MudIcon slot="icon" name="cross-large" size={20} />
          </MudButton>
        ) : (
          <MudButton
            shape="circular"
            variant="strict"
            appearance="text"
            size="sm"
            iconOnly
            className="simsm-sidebar__collapse"
            label={collapsed ? t('nav.expand') : t('nav.collapse')}
            aria-expanded={!collapsed}
            aria-controls="primary-navigation"
            onClick={onToggleCollapsed}
          >
            <MudIcon slot="icon" name="sidebar" size={20} />
          </MudButton>
        )}
      </div>

      {/* Between the head and the scrolling body, outside the account trigger
          (a select cannot live in a button) and above the navigation, where
          AGE's downward-only listbox has room to open. Draws nothing unless the
          active role holds two or more Registrars.
          per docs/shape-ops-tenant-selector.md §6 Revision 2026-10-12 */}
      <RegistrarSelector collapsed={collapsed} onExpand={onToggleCollapsed} />

      <div className="simsm-sidebar__body">
        {groups.map((group) => (
          <MudSidebarGroup
            key={group.section.id}
            {...(group.section.labelKey ? { heading: t(group.section.labelKey) } : {})}
          >
            {group.items.map(renderRow)}
          </MudSidebarGroup>
        ))}
      </div>

      <SidebarAccount />
    </MudSidebar>
  );
}
